import classNames from 'classnames';
import React, {useContext, useState} from 'react';

import {BrowseCourse, BrowseCourseProductCard} from '@udemy/browse-course';
import {CurriculumSubject} from '@udemy/career-academies';
import {ClientSideRender} from '@udemy/design-system-utils';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button, BlockList} from '@udemy/react-core-components';
import {Accordion} from '@udemy/react-reveal-components';

import {CareerAcceleratorCTAType, CTAContext} from '../../cta-context/cta-context';
import styles from './academy-curriculum.module.less';

export interface AcademyCurriculumProps {
    index: number;
    subjectOffset: number;
    course: BrowseCourse;
    curriculumSubjects: CurriculumSubject[];
    title?: string;
}

// If the index has less than two digits, pad it with a 0 to bring it up to 2 digits.
const formatIndex = (index: number) => index.toString().padStart(2, '0');

export function CurriculumSubjectTitle(props: {title: string; index: number; expanded: boolean}) {
    const {title, index, expanded} = props;

    const formattedIndex = formatIndex(index);

    return (
        <div className={styles['curriculum-subject-title']}>
            <div className={classNames(styles['title-number'], 'ud-heading-xl')}>
                {formattedIndex}
            </div>
            <div className={classNames(styles['title-text'])}>{title}</div>
            <Icon
                glyph="expand"
                className={classNames(styles['expand-icon'], {[styles['rotated']]: expanded})}
                label={false}
                color="subdued"
                size={'large'}
            />
        </div>
    );
}

export function AcademyCurriculum(props: AcademyCurriculumProps) {
    const {ctaType} = useContext(CTAContext);
    const {index, subjectOffset, course, curriculumSubjects, title} = props;
    const {gettext, interpolate} = useI18n();

    // Keep track of which sections are expanded. Necessary to enable the 'expand all' button.
    const [isSectionExpandedList, setIsSectionExpandedList] = useState(
        curriculumSubjects.map((_, idx) => idx === 0),
    );

    const canExpandMoreSections = () => isSectionExpandedList.includes(false);

    const toggleAllSections = () => {
        if (canExpandMoreSections()) {
            setIsSectionExpandedList(new Array(isSectionExpandedList.length).fill(true));
        } else {
            setIsSectionExpandedList(new Array(isSectionExpandedList.length).fill(false));
        }
    };

    const setSectionExpanded = (indexToSet: number) => {
        return (expanded: boolean) => {
            setIsSectionExpandedList(
                isSectionExpandedList.map((sectionExpansion, index) => {
                    if (index === indexToSet) {
                        return expanded;
                    }
                    return sectionExpansion;
                }),
            );
        };
    };

    const renderCurriculumContent = () => {
        return (
            <Accordion size="medium" showExpandIcon={false}>
                {curriculumSubjects.map((subject, idx) => {
                    return (
                        <Accordion.Panel
                            className={styles['content-panel']}
                            expanded={isSectionExpandedList[idx]}
                            onToggle={setSectionExpanded(idx)}
                            key={idx}
                            title={
                                <CurriculumSubjectTitle
                                    title={subject.title}
                                    index={subjectOffset + idx + 1}
                                    expanded={isSectionExpandedList[idx]}
                                />
                            }
                        >
                            <div className={styles['summary']}>
                                <BlockList size="large" padding="tight">
                                    {subject.summaryItems.map((item, idx) => {
                                        return (
                                            <BlockList.Item
                                                icon={
                                                    <Icon
                                                        glyph="tick"
                                                        color="subdued"
                                                        label={false}
                                                    />
                                                }
                                                key={idx}
                                                className={styles['summary-item']}
                                            >
                                                {item}
                                            </BlockList.Item>
                                        );
                                    })}
                                </BlockList>
                            </div>
                        </Accordion.Panel>
                    );
                })}
            </Accordion>
        );
    };

    const renderPracticeTestCurriculumContent = () => {
        return (
            <>
                {curriculumSubjects.map((subject, idx) => {
                    return (
                        <div key={idx} className={styles['practice-test-content']}>
                            <div className={classNames(styles['title-number'], 'ud-heading-xl')}>
                                {formatIndex(subjectOffset + idx + 1)}
                            </div>
                            <div className={styles['summary']}>
                                <div className={styles['title']}>{subject.title}</div>
                                <div className={styles['stats']}>{subject.summaryItems[0]}</div>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    return (
        <div
            className={classNames(styles['curriculum-container'], {
                [styles['top-space']]: index > 0,
            })}
            id={`curriculum-${course.id}`}
        >
            <div className={styles['course-overview']}>
                <div className={classNames(styles['course-count'], 'ud-text-md')}>
                    {interpolate(gettext('Course %s'), [index + 1])}
                </div>
                <div className={classNames(styles['course-title'], 'ud-heading-xl')}>
                    {title ?? ''}
                </div>
                <div className={styles['course-card']}>
                    <BrowseCourseProductCard
                        course={course}
                        showPrice={ctaType === CareerAcceleratorCTAType.PURCHASE_CTA_MX}
                        showLastUpdatedTag
                        isAsyncStaticPrice
                    />
                </div>
            </div>
            <div className={styles['curriculum-content']}>
                <div className={styles['content-lede']}>
                    <div
                        className={classNames(styles['content-lede-title'], 'ud-heading-md', {
                            [styles['practice-test']]: course.is_practice_test_course,
                        })}
                    >
                        {gettext('Content Covered')}
                    </div>
                    {!course.is_practice_test_course && (
                        <Button
                            className={classNames(styles['content-lede-button'])}
                            onClick={toggleAllSections}
                            size="small"
                            udStyle="link"
                        >
                            {canExpandMoreSections()
                                ? gettext('Expand all')
                                : gettext('Collapse all')}
                        </Button>
                    )}
                </div>
                <ClientSideRender>
                    {course.is_practice_test_course
                        ? renderPracticeTestCurriculumContent()
                        : renderCurriculumContent()}
                </ClientSideRender>
            </div>
        </div>
    );
}
